import axios from 'axios'
import { hostUI } from '../../configuraciones'
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'

const initialState = {
    records: [],
    pagination: {
        totalRecords: 0,
        totalPages: 1,
        currentPage: 1,
        limit: 10
    },
    loading: false,
    error: null
}

const OBTENER_CONDICIONES_LISTA = 'bitacoraCondiciones/obtenerLista'
const GUARDAR_CONDICIONES_RECORD = 'bitacoraCondiciones/guardarRecord'
const ACTUALIZAR_CONDICIONES_RECORD = 'bitacoraCondiciones/actualizarRecord'
const CAMBIAR_ESTATUS_CONDICIONES = 'bitacoraCondiciones/cambiarEstatus'
const ASIGNAR_INSPECTOR_CONDICIONES = 'bitacoraCondiciones/asignarInspector'
const ELIMINAR_CONDICIONES_RECORD = 'bitacoraCondiciones/eliminarRecord'

// GET list of condiciones records from BackendInspeccion (hostUI)
export const obtenerCondicionesRecords = createAsyncThunk(
    OBTENER_CONDICIONES_LISTA,
    async (params, { rejectWithValue }) => {
        try {
            let idLogin = null;
            let page = 1;
            let limit = 50;
            let search = '';

            if (typeof params === 'object' && params !== null) {
                idLogin = params.idLogin || params.id_login || null;
                page = params.page || 1;
                limit = params.limit || 50;
                search = params.search || '';
            } else {
                idLogin = params;
            }

            const queryParts = [];
            if (idLogin) queryParts.push(`idLogin=${idLogin}`);
            if (page) queryParts.push(`page=${page}`);
            if (limit) queryParts.push(`limit=${limit}`);
            if (search) queryParts.push(`search=${encodeURIComponent(search)}`);

            const queryString = queryParts.length ? `?${queryParts.join('&')}` : '';
            const url = `${hostUI}bitacora-produccion/condiciones${queryString}`;

            const res = await axios.get(url);
            return {
                records: res.data.data || [],
                pagination: res.data.pagination || { totalRecords: (res.data.data || []).length, totalPages: 1, currentPage: 1, limit: 50 }
            };
        } catch (error) {
            console.error("Error al obtener registros de condiciones ambientales:", error);
            return rejectWithValue(error.response?.data || error.message);
        }
    }
)

// POST create new record
export const guardarCondicionesRecord = createAsyncThunk(
    GUARDAR_CONDICIONES_RECORD,
    async (recordData, { rejectWithValue }) => {
        try {
            const url = `${hostUI}bitacora-produccion/condiciones`
            const res = await axios.post(url, recordData)
            return res.data.data
        } catch (error) {
            console.error("Error al crear registro de condiciones ambientales:", error)
            return rejectWithValue(error.response?.data || error.message)
        }
    }
)

// PUT update existing record
export const actualizarCondicionesRecord = createAsyncThunk(
    ACTUALIZAR_CONDICIONES_RECORD,
    async ({ id, recordData }, { rejectWithValue }) => {
        try {
            const url = `${hostUI}bitacora-produccion/condiciones/${id}`
            const res = await axios.put(url, recordData)
            return res.data.data
        } catch (error) {
            console.error("Error al actualizar registro de condiciones ambientales:", error)
            return rejectWithValue(error.response?.data || error.message)
        }
    }
)

// PUT update status & observations
export const actualizarEstatusCondicionesRecord = createAsyncThunk(
    CAMBIAR_ESTATUS_CONDICIONES,
    async ({ id, estatus, observaciones, observacionesCorreccion, observaciones_correccion }, { rejectWithValue }) => {
        try {
            const url = `${hostUI}bitacora-produccion/condiciones/${id}/estatus`
            const obsCorr = observacionesCorreccion || observaciones_correccion || observaciones;
            const res = await axios.put(url, { estatus, observaciones: obsCorr, observacionesCorreccion: obsCorr, observaciones_correccion: obsCorr })
            return res.data.data
        } catch (error) {
            console.error("Error al actualizar estatus de condiciones ambientales:", error)
            return rejectWithValue(error.response?.data || error.message)
        }
    }
)

// PUT assign inspector
export const asignarInspectorCondicionesRecord = createAsyncThunk(
    ASIGNAR_INSPECTOR_CONDICIONES,
    async ({ id, idinspector, vbo_inspector }, { rejectWithValue }) => {
        try {
            const url = `${hostUI}bitacora-produccion/condiciones/${id}/asignar-inspector`
            const res = await axios.put(url, { idinspector, vbo_inspector })
            return res.data.data
        } catch (error) {
            console.error("Error al asignar inspector a condiciones ambientales:", error)
            return rejectWithValue(error.response?.data || error.message)
        }
    }
)

// DELETE record
export const eliminarCondicionesRecord = createAsyncThunk(
    ELIMINAR_CONDICIONES_RECORD,
    async (id, { rejectWithValue }) => {
        try {
            const url = `${hostUI}bitacora-produccion/condiciones/${id}`
            await axios.delete(url)
            return id
        } catch (error) {
            console.error("Error al eliminar registro de condiciones ambientales:", error)
            return rejectWithValue(error.response?.data || error.message)
        }
    }
)

const bitacoraCondicionesSlice = createSlice({
    name: 'bitacoraCondiciones',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            // Obtener registros
            .addCase(obtenerCondicionesRecords.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(obtenerCondicionesRecords.fulfilled, (state, action) => {
                state.loading = false;
                state.records = action.payload.records;
                state.pagination = action.payload.pagination;
            })
            .addCase(obtenerCondicionesRecords.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            // Guardar registro
            .addCase(guardarCondicionesRecord.fulfilled, (state, action) => {
                if (action.payload) {
                    state.records.unshift(action.payload);
                    state.pagination.totalRecords += 1;
                }
            })
            // Actualizar registro
            .addCase(actualizarCondicionesRecord.fulfilled, (state, action) => {
                if (action.payload) {
                    const idx = state.records.findIndex(r => r.id === action.payload.id || r.id === action.payload.id_bitacora_condiciones);
                    if (idx !== -1) {
                        state.records[idx] = { ...state.records[idx], ...action.payload };
                    }
                }
            })
            // Actualizar Estatus
            .addCase(actualizarEstatusCondicionesRecord.fulfilled, (state, action) => {
                if (action.payload) {
                    const idx = state.records.findIndex(r => r.id === action.payload.id || r.id === action.payload.id_bitacora_condiciones);
                    if (idx !== -1) {
                        state.records[idx] = { ...state.records[idx], ...action.payload };
                    }
                }
            })
            // Asignar Inspector
            .addCase(asignarInspectorCondicionesRecord.fulfilled, (state, action) => {
                if (action.payload) {
                    const idx = state.records.findIndex(r => r.id === action.payload.id || r.id === action.payload.id_bitacora_condiciones);
                    if (idx !== -1) {
                        state.records[idx] = { ...state.records[idx], ...action.payload };
                    }
                }
            })
            // Eliminar registro
            .addCase(eliminarCondicionesRecord.fulfilled, (state, action) => {
                state.records = state.records.filter(r => r.id !== action.payload && r.id_bitacora_condiciones !== action.payload);
                state.pagination.totalRecords = Math.max(0, state.pagination.totalRecords - 1);
            });
    }
})

export default bitacoraCondicionesSlice.reducer;
